// Start-line DISPLAY. One grading model for every play surface (2026-07-27).
//
// The gate reports a float. That float is authoritative and is used unchanged
// wherever it feeds physics — the lateral-offset projection in targetPutt.ts
// takes tan(angleDeg) directly, SQLite stores the raw value, and replays
// re-judge off it. NOTHING here rounds, bins, or otherwise touches the number
// used for a projection. This module decides only what a HUMAN is shown.
//
// Why humans don't get the raw number: the SIGN near zero is not trustworthy —
// a putt that rolled 0.2° left can read 0.1° right on noise alone. Showing
// "+0.1°" there is worse than showing nothing: the player grooves a correction
// for a miss that never happened. So the display is categorical, and the
// direction is withheld entirely until the magnitude clears the noise floor.
//
// The two-gate campaign observed within-cell SD of about 0.19–0.26° across 581
// rolls. That evidence supports categorical feedback; it does not establish a
// guaranteed error bound or production-fleet distribution. See the current
// evidence limits in product/START_LINE_ACCURACY_ENVELOPE.md.
//
// The preregistered position-dependence theory failed and is retracted. The
// tested pair's small center difference is not a fleet-spread estimate, and the
// shared printed fixture cannot isolate gate scale from fixture scale. These UI
// thresholds are product/scoring decisions, not numeric accuracy claims.
//
// ONE deliberate exception still shows the signed scalar
// (formatStartLineDegrees): Camera Mode, where a ground-truth ramp rolls balls
// at KNOWN angles and the exact number IS the point.

// ---------------------------------------------------------------------------
// Start Line TRAINER grading (Cole 2026-07-27)
// ---------------------------------------------------------------------------
// This mode used to show a signed decimal ("+0.1°"). It was withdrawn because
// fine directional feedback near zero implies more certainty than the current
// evidence supports. Vague is survivable in a trainer; confidently backwards
// feedback is not.
//
// The structure that makes this safe:
//
//   1. The GRADE is a function of |angle| ONLY. The left/right is a separate
//      annotation. A sign error therefore cannot change a score or a streak —
//      worst case a chip points the wrong way on a putt already graded good.
//   2. No direction is shown at all inside PURE; below that display boundary we
//      say nothing rather than imply unsupported directional certainty.
//   3. Band edges are set by GOLF OUTCOME, not round numbers: each edge is the
//      angle at which the ball centre reaches the cup lip (4.25" cup, pure
//      start line, no break): approximately 20.3 / 6.8 / 4.1 ft.
//
// Do not tighten these product boundaries merely from the existing two-gate
// record. Any change needs the normal product decision plus independently
// referenced, production-representative evidence appropriate to the claim.

export type StartLineGrade = 'pure' | 'good' | 'off' | 'missed';

// ── Band edges (widened 2026-08-08; see product/START_LINE_ACCURACY_ENVELOPE.md)
//
// Scored against the REAL error distribution — 581 measured rolls across two
// gates — at the centre of each band, which is the fair test since a putt
// sitting on a boundary is a coin flip by definition:
//
//   0.5 / 1.0 / 2.0 (old):  PURE 100%  SLIGHT  60%  WIDE 100%  MAJOR 100%
//   0.5 / 1.5 / 2.5 (now):  PURE 100%  SLIGHT 100%  WIDE 100%  MAJOR 100%
//
// SLIGHT was the broken one, not PURE. At 0.5° wide against a per-putt σ of
// 0.257° it was barely two sigma across, so a putt squarely in the middle of
// SLIGHT read SLIGHT only 60% of the time — the player experienced that as the
// gate contradicting itself. PURE is UNCHANGED: spanning ±0.5° about zero it
// already contains the entire measured error distribution (every one of 100
// dead-centre rolls fell within ±0.496° of its mean), and leaving it alone
// means no historical PURE putt re-grades.

/** ≤ this = PURE, and NO direction is displayed. Holes from 20 ft. */
export const TRAINER_PURE_DEG = 0.5;
/** ≤ this = still SLIGHT (counts), direction shown. Holes from ~6.8 ft. */
export const TRAINER_GOOD_DEG = 1.5;
/** ≤ this = WIDE (streak breaks). Holes from ~4.1 ft. Beyond = MAJOR. */
export const TRAINER_OFF_DEG = 2.5;

/** Regulation cup, inches. Used for the "still holes from" outcome. */
const CUP_DIAMETER_IN = 4.25;

export interface StartLineVerdict {
  grade: StartLineGrade;
  /** null inside PURE — we do not name a direction we cannot defend. */
  direction: 'left' | 'right' | null;
  /**
   * True when this putt extends the streak (PURE and SLIGHT both count).
   *
   * ⚠️ This rides on TRAINER_GOOD_DEG, so widening SLIGHT to 1.5° on 2026-08-08
   * MOVED THE STREAK BAR from 1.0° to 1.5°. That was chosen deliberately over
   * pinning the streak to its own constant: a streak that breaks on a putt the
   * screen just labelled SLIGHT is incoherent, and what you see must match what
   * happens. Streaks are correspondingly easier — roughly 3× longer for a
   * player with ~1° of stroke dispersion. **If that proves too easy, the fix is
   * to require a LONGER streak, not to re-tighten the angle band** — difficulty
   * is a game-design dial and the band is a measurement fact.
   *
   * Done now because it is free now: 8 accounts, no customers, no streak
   * history worth preserving. After launch this would invalidate leaderboards.
   */
  counts: boolean;
}

export function gradeStartLine(angleDeg: number): StartLineVerdict {
  const abs = Math.abs(angleDeg);
  const direction = abs <= TRAINER_PURE_DEG ? null : angleDeg < 0 ? 'left' : 'right';
  const grade: StartLineGrade = abs <= TRAINER_PURE_DEG
    ? 'pure'
    : abs <= TRAINER_GOOD_DEG
      ? 'good'
      : abs <= TRAINER_OFF_DEG
        ? 'off'
        : 'missed';
  return { grade, direction, counts: grade === 'pure' || grade === 'good' };
}

/** The single gate for "does this putt extend the Start Line streak". The store
 *  used to inline `Math.abs(angleDeg) <= 1.0`; routing it here keeps the streak
 *  and the on-screen verdict from ever disagreeing. */
export function startLineCounts(angleDeg: number): boolean {
  return gradeStartLine(angleDeg).counts;
}

export const START_LINE_GRADE_LABEL: Record<StartLineGrade, string> = {
  // The crown is PURE's badge everywhere (Cole 2026-07-29): green says "you
  // count", the crown says "you're the king band". Display-only — grade keys
  // are what's stored and compared.
  pure: 'PURE 👑',
  // Renamed 2026-08-01 (Cole): GOOD -> SLIGHT, MISSED -> MAJOR. Labels only —
  // the stored grade keys and every threshold are untouched, so history,
  // streaks, and leaderboards re-render under the new names with no migration.
  // Renamed again 2026-08-02 (Cole): OFF -> WIDE. Final tier ladder is
  // PURE -> SLIGHT -> WIDE -> MAJOR. Labels only, keys and thresholds
  // untouched as before.
  good: 'SLIGHT',
  off: 'WIDE',
  missed: 'MAJOR',
};

/**
 * Longest putt this start line still holes: the distance at which the ball
 * centre reaches the cup lip. Pure start-line error — no break, no speed
 * effect, so it is an upper bound and reads as one ("holes from 12 ft").
 *
 * Returns null for a non-finite angle. A dead-straight putt would be infinite,
 * so it is capped rather than rendered as ∞.
 */
export const TRAINER_MAX_HOLE_FT = 40;

export function holesFromFt(angleDeg: number): number | null {
  if (!Number.isFinite(angleDeg)) return null;
  const abs = Math.abs(angleDeg);
  if (abs < 1e-6) return TRAINER_MAX_HOLE_FT;
  const ft = (CUP_DIAMETER_IN / 2) / Math.tan((abs * Math.PI) / 180) / 12;
  return Math.min(TRAINER_MAX_HOLE_FT, ft);
}

// The app-wide category is just (grade × direction) flattened, so every surface
// — Target, Challenge, Sim 9, the putt log, the 3D scene, the summary chart —
// bins a putt exactly the way the Start Line trainer does. PURE has no
// direction variant by construction: that is the band where the sign is inside
// the noise floor and is deliberately not claimed.
export type StartLineCategory =
  | 'pure'
  | 'good_left' | 'good_right'
  | 'off_left' | 'off_right'
  | 'missed_left' | 'missed_right';

// Sign convention: firmware reports negative angleDeg for a putt that
// starts LEFT of the target line, positive for RIGHT. This classifier and
// every HUD that calls it (AngleIndicator, PuttLog, PuttCard, landscape
// pills, summary bar chart) share one convention. If anything flips L/R,
// fix the consumer — don't flip this sign.
export function classifyStartLine(angleDeg: number): StartLineCategory {
  const { grade, direction } = gradeStartLine(angleDeg);
  if (grade === 'pure' || direction === null) return 'pure';
  return `${grade}_${direction}` as StartLineCategory;
}

const CATEGORY_LABEL: Record<StartLineCategory, { full: string; short: string; spoken: string }> = {
  pure:         { full: 'Pure 👑',      short: 'Pure 👑', spoken: 'on line' },
  good_left:    { full: '<- Slight',     short: '<- Slight', spoken: 'slightly left' },
  good_right:   { full: 'Slight ->',     short: 'Slight ->', spoken: 'slightly right' },
  off_left:     { full: '<-- Wide',      short: '<-- Wide',  spoken: 'wide left' },
  off_right:    { full: 'Wide -->',      short: 'Wide -->',  spoken: 'wide right' },
  missed_left:  { full: '<--- Major',    short: '<--- Major', spoken: 'major miss, left' },
  missed_right: { full: 'Major --->',    short: 'Major --->', spoken: 'major miss, right' },
};

export function formatStartLine(angleDeg: number | null): string {
  if (angleDeg === null) return '--';
  return CATEGORY_LABEL[classifyStartLine(angleDeg)].full;
}

// Shorter display used in compact cards / log rows.
export function formatStartLineShort(angleDeg: number | null): string {
  if (angleDeg === null) return '--';
  return CATEGORY_LABEL[classifyStartLine(angleDeg)].short;
}

/** Screen-reader phrasing. The visible labels are terse enough to be cryptic
 *  read aloud ("start line pure"), so the spoken form is written as English. */
export function formatStartLineSpoken(angleDeg: number | null): string {
  if (angleDeg === null) return 'unavailable';
  return CATEGORY_LABEL[classifyStartLine(angleDeg)].spoken;
}

// Signed decimal start-line angle for the Camera Mode marketing readout,
// e.g. "-2.3°" (started LEFT of target) or "+1.4°" (RIGHT). One decimal place,
// explicit leading sign so "negative = left, positive = right" reads
// unambiguously on camera; a near-zero angle rounds to a plain "0.0°" (no
// sign, no negative-zero). Sign convention matches the firmware and
// classifyStartLine above: negative angleDeg = LEFT, positive = RIGHT.
export function formatStartLineDegrees(angleDeg: number | null): string {
  if (angleDeg === null || !Number.isFinite(angleDeg)) return '--';
  const rounded = Math.round(angleDeg * 10) / 10;
  if (rounded === 0) return '0.0°'; // also collapses -0 (rounded === 0 is true)
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toFixed(1)}°`;
}

// Token key into `theme/tokens` `colors`. Keeping the mapping here rather
// than in components so every surface renders the same category color.
// PURE was brand gold through 2026-07-29; on device it read as a WARNING tint
// next to green GOOD ("why is pure yellow?" — Cole). Now PURE is the same
// green as GOOD — both "count" — and the 👑 in its label carries the
// best-band distinction instead of a color doing it.
/** Grade for a CATEGORY key (missed_left -> missed). For surfaces bucketed
 *  by category rather than raw angle (the dispersion bar chart). */
export function startLineCategoryGrade(category: StartLineCategory): StartLineGrade {
  if (category === 'pure') return 'pure';
  return category.replace(/_(left|right)$/, '') as StartLineGrade;
}

/** Chart-length label for a category, derived from the SAME tables as every
 *  other surface — a rename in this file reaches the chart automatically
 *  (the parallel map it replaced shipped GOOD/MISS after two renames). */
export function startLineCategoryChartLabel(category: StartLineCategory): string {
  if (category === 'pure') return `${START_LINE_GRADE_LABEL.pure}`;
  return CATEGORY_LABEL[category].short.toUpperCase();
}

/** Tone token for a category — same palette switch as startLineToneKey. */
export function startLineCategoryToneKey(
  category: StartLineCategory
): 'proximityGood' | 'proximityMid' | 'proximityFar' {
  switch (startLineCategoryGrade(category)) {
    case 'pure':
    case 'good':
      return 'proximityGood';
    case 'off':
      return 'proximityMid';
    case 'missed':
      return 'proximityFar';
  }
}

export function startLineToneKey(
  angleDeg: number | null
): 'gold' | 'proximityGood' | 'proximityMid' | 'proximityFar' | 'textMuted' {
  if (angleDeg === null) return 'textMuted';
  switch (gradeStartLine(angleDeg).grade) {
    case 'pure':   return 'proximityGood';
    case 'good':   return 'proximityGood';
    case 'off':    return 'proximityMid';
    case 'missed': return 'proximityFar';
  }
}
